import * as React from 'react';

/**
 * Shared drag-tracking primitives used by both the parent-frame `PanSurface`
 * overlay and the iframe-side `useMiddleMousePan` hook. Both flows share the
 * same shape -- anchor on a starting position, diff each subsequent move,
 * batch deltas with `requestAnimationFrame`, flush on terminate -- but their
 * event wiring is environment-specific (React pointer events vs. native
 * mouse events on the iframe document). This hook owns the state machine;
 * callers wire the events.
 *
 * The hook is intentionally environment-agnostic: no `window`/`document`
 * access, no DOM listeners, no pointer-capture. Callers attach their own
 * listeners and decide *when* to call `beginDrag` / `applyMove` / `endDrag`.
 * That keeps recovery (`blur`, `visibilitychange`, `mouseleave` + buttons
 * check) per-environment while the inner state machine stays unified.
 */
type PanDragLifecycleOptions = {
  /**
   * Called with negated cumulative deltas (`-dx`, `-dy`) once per
   * animation frame while a drag is in flight. The negation matches the
   * pan convention used by `canvasViewportPanned` -- dragging the cursor
   * right shifts the canvas right, which in viewport coordinates is a
   * negative translation.
   */
  onPanned?: (dx: number, dy: number) => void;
  /**
   * Called once per `endDrag()` invocation when a drag was active.
   * Recovery paths (`blur`, `visibilitychange`, etc.) end up here too.
   */
  onPanEnded?: () => void;
};

export type PanDragLifecycle = {
  /** Whether a drag is currently in flight. */
  isDragging: () => boolean;
  /**
   * Anchor a drag at the supplied screen coordinates. Idempotent if a drag
   * is already in flight (the new anchor replaces the old).
   */
  beginDrag: (clientX: number, clientY: number) => void;
  /**
   * Accumulate a move relative to the current anchor and schedule a
   * single RAF flush. No-op when no drag is active.
   */
  applyMove: (clientX: number, clientY: number) => void;
  /**
   * Terminate the active drag: cancel any pending RAF, flush any
   * remaining delta, and fire `onPanEnded`. Returns `true` when a drag
   * was actually ended (so callers can short-circuit when nothing was
   * active).
   */
  endDrag: () => boolean;
  /**
   * Cancel any pending RAF without flushing. Useful for component
   * unmount paths where the caller doesn't want a late delta to fire
   * after teardown.
   */
  cancelPendingFrame: () => void;
};

export const usePanDragLifecycle = (
  options: PanDragLifecycleOptions
): PanDragLifecycle => {
  const isDraggingRef = React.useRef(false);
  const lastPositionRef = React.useRef<{x: number; y: number} | null>(null);
  const pendingDeltaRef = React.useRef<{dx: number; dy: number}>({
    dx: 0,
    dy: 0,
  });
  const rafIdRef = React.useRef<number | null>(null);

  // Mirror callbacks via refs so consumers don't need to memoize them.
  const onPannedRef = React.useRef(options.onPanned);
  onPannedRef.current = options.onPanned;
  const onPanEndedRef = React.useRef(options.onPanEnded);
  onPanEndedRef.current = options.onPanEnded;

  const flushPendingDelta = React.useCallback(() => {
    const {dx, dy} = pendingDeltaRef.current;
    if (dx !== 0 || dy !== 0) {
      onPannedRef.current?.(-dx, -dy);
      pendingDeltaRef.current = {dx: 0, dy: 0};
    }
    rafIdRef.current = null;
  }, []);

  const cancelPendingFrame = React.useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const beginDrag = React.useCallback((clientX: number, clientY: number) => {
    isDraggingRef.current = true;
    lastPositionRef.current = {x: clientX, y: clientY};
    pendingDeltaRef.current = {dx: 0, dy: 0};
  }, []);

  const applyMove = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!isDraggingRef.current || !lastPositionRef.current) return;

      const dx = clientX - lastPositionRef.current.x;
      const dy = clientY - lastPositionRef.current.y;
      lastPositionRef.current = {x: clientX, y: clientY};

      pendingDeltaRef.current.dx += dx;
      pendingDeltaRef.current.dy += dy;

      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(flushPendingDelta);
      }
    },
    [flushPendingDelta]
  );

  const endDrag = React.useCallback((): boolean => {
    if (!isDraggingRef.current) return false;

    cancelPendingFrame();
    flushPendingDelta();

    isDraggingRef.current = false;
    lastPositionRef.current = null;

    onPanEndedRef.current?.();
    return true;
  }, [cancelPendingFrame, flushPendingDelta]);

  const isDragging = React.useCallback(() => isDraggingRef.current, []);

  // Cancel any orphaned RAF if the hook is torn down mid-drag (e.g. the
  // component unmounts while a delta is pending). Callers that need to
  // *complete* a drag on unmount should call `endDrag()` explicitly before
  // unmounting -- this cleanup just prevents callbacks firing after teardown.
  React.useEffect(() => {
    return () => {
      cancelPendingFrame();
    };
  }, [cancelPendingFrame]);

  return {
    isDragging,
    beginDrag,
    applyMove,
    endDrag,
    cancelPendingFrame,
  };
};
